import React, { useEffect, useState } from "react";
import { Table, Pagination, Card, Nav } from "@themesberg/react-bootstrap";
import { showPageNumbers } from "../../utils/table";
import Select from "react-select";
import { fetchTrancheAssets } from "../../service/api";

const paginationOptions = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 25, label: "25" },
];

const TranchAssetTable = ({ assets = [], trancheId, totalDocs }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const isRemote = Boolean(trancheId);
  const initialTotalDocs =
    typeof totalDocs === "number" ? totalDocs : assets.length;
  const isInitialRemotePage = isRemote && currentPage === 1 && itemsPerPage === 5;
  const [remoteAssets, setRemoteAssets] = useState([]);

  const displayedAssets = isRemote
    ? isInitialRemotePage
      ? assets
      : remoteAssets
    : assets;

  const displayedTotalDocs = isRemote ? initialTotalDocs : assets.length;

  const totalPages = Math.ceil(displayedTotalDocs / itemsPerPage) || 1;
  const showPaginationNumbers = totalPages > 1;

  const getAssetName = (assetData) => {
    if (!assetData || typeof assetData !== "object") return "N/A";
    const values = Object.values(assetData);
    return values.length > 0 ? values[0] : "N/A";
  };

  // Calculate paginated assets
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssets = isRemote
    ? displayedAssets
    : displayedAssets.slice(startIndex, startIndex + itemsPerPage);

  const handleItemsPerPageChange = (selectedOption) => {
    if (!selectedOption) return;
    setItemsPerPage(selectedOption.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      return;
    }

    if (!isRemote || isInitialRemotePage) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await fetchTrancheAssets(trancheId, {
          page: currentPage,
          limit: itemsPerPage,
        });

        if (cancelled) return;
        const nextAssets = res?.data?.assets || [];
        setRemoteAssets(nextAssets);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching tranche assets:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentPage, isInitialRemotePage, isRemote, itemsPerPage, totalPages, trancheId]);

  return (
    <div className="scrollable-table-container2 ">
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-4">
          <Card.Title className="mb-3">
            Attached Assets ({displayedTotalDocs})
          </Card.Title>
          {displayedTotalDocs === 0 ? (
            <p className="bg-grey text-muted">
              No assets attached to this tranche.
            </p>
          ) : (
            <Table hover className="user-table align-items-center table-striped">
              <thead className="table-light">
                <tr>
                  <th className="break-words">Asset ID</th>
                  <th className="break-words">Deal Name</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAssets.map((asset, index) => (
                  <tr
                    key={asset._id || index}
                    className={index % 2 === 0 ? "even-row" : "odd-row"}
                  >
                    <td className="p-2 break-words">
                      <code className="text-primary">{asset._id}</code>
                    </td>
                    <td className="p-2 break-words whitespace-normal">
                      {getAssetName(asset.assetData)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {displayedTotalDocs > 0 && (
          <Card.Footer className="px-3 border-0 d-lg-flex justify-content-between">
            <Nav
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                background: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderTop: "1px solid #dee2e6",
                width: "100%",
              }}
            >
              <Pagination className="m-4">
                <Pagination.Prev
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Pagination.Prev>
                {showPaginationNumbers && (
                  <>
                    {currentPage > 4 && (
                      <>
                        <Pagination.Item onClick={() => setCurrentPage(1)}>
                          1
                        </Pagination.Item>
                        <Pagination.Ellipsis disabled />
                      </>
                    )}
                    {[...Array(totalPages)].map((_, page) => {
                      if (showPageNumbers(page, currentPage, totalPages)) {
                        return (
                          <Pagination.Item
                            key={page + 1}
                            active={page + 1 === currentPage}
                            onClick={() => setCurrentPage(page + 1)}
                          >
                            {page + 1}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}
                    {currentPage < totalPages - 3 && (
                      <>
                        <Pagination.Ellipsis disabled />
                        <Pagination.Item
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Pagination.Item>
                      </>
                    )}
                  </>
                )}
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Next
                </Pagination.Next>
              </Pagination>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Items Per Page
                <Select
                  options={paginationOptions}
                  defaultValue={paginationOptions.find(
                    (opt) => opt.value === itemsPerPage
                  )}
                  onChange={handleItemsPerPageChange}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "32px",
                      height: "32px",
                      fontSize: "14px",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                      padding: "0 6px",
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: "14px",
                      padding: "6px 12px",
                      minHeight: "28px",
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                  }}
                  menuPlacement="top"
                />
              </div>
            </Nav>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default TranchAssetTable;
